import _slicedToArray from "/codebuild/output/src3402/src/s3/01/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { DEBUG_BUILD } from './debug-build.js';
import { consoleSandbox, logger } from './logger.js';

/** Regular expression used to parse a Dsn. */
var DSN_REGEX = /^(?:(\w+):)\/\/(?:(\w+)(?::(\w+)?)?@)([\w.-]+)(?::(\d+))?\/(.+)/;
function isValidProtocol(protocol) {
  return protocol === 'http' || protocol === 'https';
}

/**
 * Renders the string representation of this Dsn.
 *
 * By default, this will render the public representation without the password
 * component. To get the deprecated private representation, set `withPassword`
 * to true.
 *
 * @param withPassword When set to true, the password will be included.
 */
function dsnToString(dsn) {
  var withPassword = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var host = dsn.host,
    path = dsn.path,
    pass = dsn.pass,
    port = dsn.port,
    projectId = dsn.projectId,
    protocol = dsn.protocol,
    publicKey = dsn.publicKey;
  return "".concat(protocol, "://").concat(publicKey).concat(withPassword && pass ? ":".concat(pass) : '') + "@".concat(host).concat(port ? ":".concat(port) : '', "/").concat(path ? "".concat(path, "/") : path).concat(projectId);
}

/**
 * Parses a Dsn from a given string.
 *
 * @param str A Dsn as string
 * @returns Dsn as DsnComponents or undefined if @param str is not a valid DSN string
 */
function dsnFromString(str) {
  var match = DSN_REGEX.exec(str);
  if (!match) {
    // This should be logged to the console
    consoleSandbox(function () {
      // eslint-disable-next-line no-console
      console.error("Invalid Sentry Dsn: ".concat(str));
    });
    return undefined;
  }
  var _match$slice = match.slice(1),
    _match$slice2 = _slicedToArray(_match$slice, 6),
    protocol = _match$slice2[0],
    publicKey = _match$slice2[1],
    _match$slice2$ = _match$slice2[2],
    pass = _match$slice2$ === void 0 ? '' : _match$slice2$,
    host = _match$slice2[3],
    _match$slice2$2 = _match$slice2[4],
    port = _match$slice2$2 === void 0 ? '' : _match$slice2$2,
    lastPath = _match$slice2[5];
  var path = '';
  var projectId = lastPath;
  var split = projectId.split('/');
  if (split.length > 1) {
    path = split.slice(0, -1).join('/');
    projectId = split.pop();
  }
  if (projectId) {
    var projectMatch = projectId.match(/^\d+/);
    if (projectMatch) {
      projectId = projectMatch[0];
    }
  }
  return dsnFromComponents({
    host: host,
    pass: pass,
    path: path,
    projectId: projectId,
    port: port,
    protocol: protocol,
    publicKey: publicKey
  });
}
function dsnFromComponents(components) {
  return {
    protocol: components.protocol,
    publicKey: components.publicKey || '',
    pass: components.pass || '',
    host: components.host,
    port: components.port || '',
    path: components.path || '',
    projectId: components.projectId
  };
}
function validateDsn(dsn) {
  if (!DEBUG_BUILD) {
    return true;
  }
  var port = dsn.port,
    projectId = dsn.projectId,
    protocol = dsn.protocol;
  var requiredComponents = ['protocol', 'publicKey', 'host', 'projectId'];
  var hasMissingRequiredComponent = requiredComponents.find(function (component) {
    if (!dsn[component]) {
      logger.error("Invalid Sentry Dsn: ".concat(component, " missing"));
      return true;
    }
    return false;
  });
  if (hasMissingRequiredComponent) {
    return false;
  }
  if (!projectId.match(/^\d+$/)) {
    logger.error("Invalid Sentry Dsn: Invalid projectId ".concat(projectId));
    return false;
  }
  if (!isValidProtocol(protocol)) {
    logger.error("Invalid Sentry Dsn: Invalid protocol ".concat(protocol));
    return false;
  }
  if (port && isNaN(parseInt(port, 10))) {
    logger.error("Invalid Sentry Dsn: Invalid port ".concat(port));
    return false;
  }
  return true;
}

/**
 * Creates a valid Sentry Dsn object, identifying a Sentry instance and project.
 * @returns a valid DsnComponents object or `undefined` if @param from is an invalid DSN source
 */
function makeDsn(from) {
  var components = typeof from === 'string' ? dsnFromString(from) : dsnFromComponents(from);
  if (!components || !validateDsn(components)) {
    return undefined;
  }
  return components;
}
export { dsnFromString, dsnToString, makeDsn };